import React, { useState, useRef, useEffect } from "react";
import { Search, Loader2, Plus, X, Building2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Searchable Developer / Project picker (Phase 1).
 * - Selects a CANONICAL record, or creates a CANDIDATE via "Add".
 * - "Not Applicable" clears the selection (persists as null id + null name).
 * value: { id: string|null, name: string|null }
 */
export const DevProjectPicker = ({ kind, value, onChange, developerId, disabled, testid }) => {
  const isDev = kind === "developer";
  const naLabel = isDev ? "Independent / Not Applicable" : "Not Applicable";
  const searchPath = isDev ? "/developers" : "/projects";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const params = { q: q.trim() };
        if (!isDev && developerId) params.developer_id = developerId;
        const { data } = await api.get(searchPath, { params });
        setResults(data || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 260);
    return () => clearTimeout(timer.current);
  }, [q, open, developerId]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (rec) => { onChange(rec); setOpen(false); setQ(""); };
  const clear = () => onChange({ id: null, name: null });

  const addNew = async () => {
    const name = q.trim();
    if (!name) return;
    setAdding(true);
    try {
      const body = isDev ? { name } : { name, developer_id: developerId || "" };
      const { data } = await api.post(searchPath, body);
      pick({ id: data.id, name: data.name });
    } catch { /* surfaced by caller toasts elsewhere */ } finally { setAdding(false); }
  };

  const exactExists = results.some((r) => r.name.toLowerCase() === q.trim().toLowerCase());

  if (value?.name) {
    return (
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-white px-3 h-11" data-testid={`${testid}-current`}>
        <Building2 className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-medium text-slate-700 truncate">{value.name}</span>
        {!disabled && <button type="button" data-testid={`${testid}-change`} onClick={clear} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Change</button>}
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <div className="relative mt-1.5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={`${testid}-input`} value={q} disabled={disabled}
          onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)}
          placeholder={isDev ? "Search a developer…" : "Search a project / development…"}
          className="w-full h-11 pl-9 pr-9 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-100" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {open && !disabled && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-border bg-white shadow-lg" data-testid={`${testid}-results`}>
          <li>
            <button type="button" data-testid={`${testid}-na`} onClick={() => pick({ id: null, name: null })}
              className="w-full px-3 py-2.5 text-left text-sm text-slate-500 hover:bg-slate-50 border-b border-border">{naLabel}</button>
          </li>
          {results.map((r) => (
            <li key={r.id}>
              <button type="button" data-testid={`${testid}-option-${r.id}`} onClick={() => pick({ id: r.id, name: r.name })}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50">
                <Building2 className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{r.name}</span>
              </button>
            </li>
          ))}
          {q.trim() && !exactExists && (
            <li>
              <button type="button" data-testid={`${testid}-add`} onClick={addNew} disabled={adding}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-primary/5 border-t border-border">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add “{q.trim()}”
              </button>
            </li>
          )}
          {!loading && !results.length && !q.trim() && (
            <li className="px-3 py-2.5 text-xs text-slate-400">Type to search{isDev ? " developers" : " projects"}…</li>
          )}
        </ul>
      )}
    </div>
  );
};
